import { ACTIVE_VEHICLE_TYPES, createEmptyCounts } from '@/config/vehicleTypes';
import type { CountEvent, VehicleCounts } from '@/types/survey';

/**
 * Pure counting logic. Kept free of React and Dexie so the data-integrity rules
 * (`count >= 0`, `total = sum of categories`) can be unit tested directly.
 */

/** Sum of every category. */
export function totalCount(counts: VehicleCounts): number {
  let total = 0;
  for (const value of Object.values(counts)) {
    total += Number.isFinite(value) ? Math.max(0, value) : 0;
  }
  return total;
}

/** Returns a new counts object with `vehicleTypeId` incremented by `by`. */
export function increment(counts: VehicleCounts, vehicleTypeId: string, by = 1): VehicleCounts {
  const current = counts[vehicleTypeId] ?? 0;
  return { ...counts, [vehicleTypeId]: Math.max(0, current + by) };
}

/** Decrement that can never produce a negative count. */
export function decrement(counts: VehicleCounts, vehicleTypeId: string, by = 1): VehicleCounts {
  return increment(counts, vehicleTypeId, -by);
}

/**
 * Rebuild counts from the append-only event log. This is the authoritative
 * recovery path after a refresh, a crash or a PWA restart.
 */
export function replayCounts(events: CountEvent[]): VehicleCounts {
  const counts = createEmptyCounts();
  for (const event of events) {
    const current = counts[event.vehicleTypeId] ?? 0;
    counts[event.vehicleTypeId] = Math.max(0, current + event.delta);
  }
  return counts;
}

/**
 * The most recent increment that has not already been undone — i.e. what
 * "Undo Last Count" should reverse. Returns null when there is nothing to undo.
 */
export function findLastUndoableEvent(events: CountEvent[]): CountEvent | null {
  for (let i = events.length - 1; i >= 0; i--) {
    const event = events[i];
    if (event.delta === 1 && !event.undone) return event;
  }
  return null;
}

/** Fills in any missing categories with 0 and drops unknown ids. */
export function normalizeCounts(counts: VehicleCounts | undefined): VehicleCounts {
  const normalized = createEmptyCounts();
  if (!counts) return normalized;
  for (const type of ACTIVE_VEHICLE_TYPES) {
    const value = counts[type.id];
    normalized[type.id] = Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;
  }
  return normalized;
}
